'use client';

import type { User } from '@api/models';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BookingData, getUnifiedBookings } from '@website/services/bookings.service';

export interface UseActivityBookingsReturn {
  upcomingBookings: BookingData[];
  pastBookings: BookingData[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Hook to fetch user bookings for a specific activity.
 * Returns upcoming and past bookings filtered by activity ID.
 *
 * @param activityId - The ID of the activity to filter bookings for
 * @param user - The current user (null if not authenticated)
 * @returns Object containing upcoming/past bookings and loading states
 */
export const useActivityBookings = (activityId: string, user: User | null | undefined): UseActivityBookingsReturn => {
  const query = useQuery({
    queryKey: ['activity-bookings', activityId, user?.id],
    queryFn: async () => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const userType = user.isProfessional ? 'professional' : 'user';
      return getUnifiedBookings(userType, {
        activityId
      });
    },
    enabled: !!user && !!activityId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  const result = useMemo(() => {
    if (!query.data) {
      return {
        upcomingBookings: [],
        pastBookings: []
      };
    }

    return {
      upcomingBookings: query.data.upcoming,
      pastBookings: query.data.past
    };
  }, [query.data]);

  return {
    ...result,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error
  };
};
